<!-- BACKDROP -->
<div
  *ngIf="isPanelOpen"
  class="panel-backdrop"
  [@fadeBackdrop]
  (click)="closePanel()">
</div>

<!-- PANEL -->
<div
  *ngIf="isPanelOpen"
  class="panel-container"
  [@slidePanel]
  [class.expanded]="selectedNote">

  <!-- ============================================================ -->
  <!-- LEFT SIDE: Notes List                                        -->
  <!-- ============================================================ -->
  <div class="notes-list-section" [class.hidden-mobile]="selectedNote">

    <!-- Panel Header -->
    <div class="panel-header">
      <div class="panel-header-left">
        <mat-icon class="panel-header-icon">assignment</mat-icon>
        <h3>Notes</h3>
        <span class="notes-count">{{ taggedNotes.length }}</span>
      </div>
      <div class="panel-header-actions">
        <button mat-icon-button matTooltip="Open all notes in new page" (click)="openAllNotesPage()">
          <mat-icon>open_in_new</mat-icon>
        </button>
        <button mat-icon-button matTooltip="Close" (click)="closePanel()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Search & Filters -->
    <div class="search-section">
      <div class="search-input-wrapper">
        <mat-icon class="search-icon">search</mat-icon>
        <input
          [formControl]="searchControl"
          placeholder="Search notes..."
          class="search-input" />
        <button
          *ngIf="searchControl.value"
          mat-icon-button
          class="search-clear"
          (click)="searchControl.setValue('')">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="filter-chips">
        <button
          *ngFor="let status of ['all', 'active', 'completed']"
          class="filter-chip"
          [class.active]="filterStatus === status"
          (click)="setFilter(status)">
          {{ status | titlecase }}
        </button>
      </div>
    </div>

    <!-- Notes List -->
    <div class="notes-list-container">
      <!-- Loading -->
      <div *ngIf="isLoadingList" class="loading-container">
        <mat-spinner diameter="32"></mat-spinner>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoadingList && taggedNotes.length === 0" class="empty-state">
        <mat-icon class="empty-icon">speaker_notes_off</mat-icon>
        <p>No notes found</p>
        <span class="empty-hint">You'll see notes here when someone tags you</span>
      </div>

      <!-- Notes -->
      <div
        *ngFor="let note of taggedNotes; trackBy: trackByListItem"
        class="note-list-item"
        [class.selected]="selectedNote?.noteId === note.noteId"
        [class.has-unread]="note.unreadCount > 0"
        (click)="selectNote(note)">

        <div class="note-avatar">
          <span class="avatar-text" [style.background-color]="'hsl(' + (note.noteId * 47 % 360) + ', 55%, 55%)'">
            {{ note.clientName?.charAt(0) || 'N' }}{{ note.title?.charAt(0) || '' }}
          </span>
        </div>

        <div class="note-info">
          <div class="note-info-top">
            <span class="note-title">{{ note.title }}<span *ngIf="note.year && !titleHasYear(note)"> - {{ note.year }}</span></span>
            <span class="note-time">{{ formatTimeAgo(note.lastActivityDate) }}</span>
          </div>
          <div class="note-client">{{ note.clientName }}</div>
          <div class="note-ref" *ngIf="note.referenceNumber">
            <mat-icon>tag</mat-icon>
            <app-reference-number [referenceNumber]="note.referenceNumber"></app-reference-number>
            <span *ngIf="note.year" class="note-year">&bull; {{ note.year }}</span>
          </div>
          <div class="note-info-bottom">
            <span class="note-preview">{{ stripHtml(note.lastMessage) }}</span>
            <span *ngIf="note.unreadCount > 0" class="unread-badge">
              {{ note.unreadCount }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- RIGHT SIDE: Note Detail (shown when a note is selected)      -->
  <!-- ============================================================ -->
  <div *ngIf="selectedNote" class="note-detail-section">

    <!-- Detail Header -->
    <div class="detail-header">
      <div class="detail-header-left">
        <button mat-icon-button (click)="goBackToList()" class="back-btn">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div class="detail-avatar">
          <span class="avatar-text small" [style.background-color]="'hsl(' + (selectedNote.noteId * 47 % 360) + ', 55%, 55%)'">
            {{ selectedNote.clientName?.charAt(0) || 'N' }}{{ selectedNote.title?.charAt(0) || '' }}
          </span>
        </div>
        <div class="detail-meta">
          <h4 class="clickable" (click)="openFullViewNewTab()" matTooltip="Open full view in new tab">
            {{ selectedNote.title }}<span *ngIf="selectedNote.year && !titleHasYear(selectedNote)"> - {{ selectedNote.year }}</span>
            <mat-icon class="open-icon">open_in_new</mat-icon>
          </h4>
          <div class="detail-sub">
            <span class="detail-client clickable" (click)="openClientPage(selectedNote)">{{ selectedNote.clientName }}</span>
            <span class="separator">&bull;</span>
            <span class="detail-dept">{{ selectedNote.departmentName }}</span>
          </div>
          <div class="detail-ref" *ngIf="selectedNote.referenceNumber">
            <mat-icon>tag</mat-icon>
            <app-reference-number [referenceNumber]="selectedNote.referenceNumber"></app-reference-number>
          </div>
        </div>
      </div>
      <div class="detail-header-right">
        <!-- Time Tracker -->
        <div class="time-tracker">
          <div class="timer-display" [class.running]="isTimerRunning">
            <mat-icon class="timer-icon">timer</mat-icon>
            <span class="timer-value">{{ formatElapsedTime(timerElapsedSeconds) }}</span>
          </div>
          <button
            class="billable-btn"
            [class.active]="timerBillable"
            (click)="timerBillable = !timerBillable"
            matTooltip="Click to toggle">
            {{ timerBillable ? 'Billable' : 'Non billable' }}
          </button>
          <button
            class="timer-btn start"
            *ngIf="!isTimerRunning"
            (click)="startTimer()"
            matTooltip="Start tracking time">
            <mat-icon>play_arrow</mat-icon>
          </button>
          <button
            class="timer-btn stop"
            *ngIf="isTimerRunning"
            (click)="stopTimer()"
            matTooltip="Stop and save to timesheet">
            <mat-icon>stop</mat-icon>
          </button>
          <button
            class="timer-btn cancel"
            *ngIf="isTimerRunning"
            (click)="cancelTimer()"
            matTooltip="Cancel timer">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div class="detail-header-actions">
          <button mat-icon-button matTooltip="Open in new tab" (click)="openFullViewNewTab()">
            <mat-icon>open_in_new</mat-icon>
          </button>
          <button mat-icon-button matTooltip="Copy link to this note" (click)="copyNoteUrl()">
            <mat-icon>content_copy</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Timer Description Input -->
    <div class="timer-description-bar" *ngIf="showTimerDescription">
      <mat-icon>edit_note</mat-icon>
      <input
        class="timer-description-input"
        [(ngModel)]="timerDescription"
        placeholder="Add comments for timesheet entry..."
        (keydown.enter)="stopTimer()" />
      <button class="timer-save-btn" (click)="stopTimer()" matTooltip="Save to timesheet">
        <mat-icon>check</mat-icon> Save
      </button>
      <button class="timer-cancel-btn" (click)="cancelTimer()" matTooltip="Cancel">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Tabs -->
    <div class="detail-tabs">
      <button
        *ngFor="let tab of tabsList"
        class="tab-btn"
        [class.active]="activeTab === tab.key"
        (click)="setActiveTab(tab.key)">
        <mat-icon>{{ tab.icon }}</mat-icon>
        {{ tab.label }}
      </button>
    </div>

    <!-- Tab Content Area -->
    <div class="tab-content">

      <!-- ======================== DISCUSSION TAB ======================== -->
      <ng-container *ngIf="activeTab === 'discussion'">
        <div class="chat-container" #chatContainer>
          <!-- Loading Thread -->
          <div *ngIf="isLoadingThread" class="loading-container">
            <mat-spinner diameter="32"></mat-spinner>
          </div>

          <!-- Chat Messages -->
          <ng-container *ngIf="!isLoadingThread && discussionNotes.length > 0">
            <div
              *ngFor="let note of discussionNotes; trackBy: trackByNoteId"
              class="chat-message"
              [class.own-message]="isOwnMessage(note.userID)">

              <!-- Reply indicator -->
              <div
                *ngIf="note.parentMeetingNoteId && note.parentMeetingNoteId !== noteThread?.id"
                class="reply-indicator">
                <mat-icon>reply</mat-icon>
                <span>Reply to {{ getParentAuthorName(note.parentMeetingNoteId) }}</span>
              </div>

              <div class="message-row" [class.own]="isOwnMessage(note.userID)">
                <!-- Avatar (left side for others) -->
                <div *ngIf="!isOwnMessage(note.userID)" class="message-avatar">
                  <span class="avatar-text tiny" [style.background-color]="'hsl(' + (note.userID * 73 % 360) + ', 50%, 50%)'">
                    {{ getInitials(note.userID) }}
                  </span>
                </div>

                <div class="message-content">
                  <!-- Sender name & time -->
                  <div class="message-meta" [class.align-right]="isOwnMessage(note.userID)">
                    <span class="sender-name" [class.own-name]="isOwnMessage(note.userID)">
                      {{ isOwnMessage(note.userID) ? 'You' : getUserName(note.userID) }}
                    </span>
                    <span *ngIf="getCompanyName(note.companyId)" class="company-name">{{ getCompanyName(note.companyId) }}</span>
                    <span class="message-time">{{ formatDate(note.date) }}</span>
                  </div>

                  <!-- Message bubble -->
                  <div
                    class="message-bubble"
                    [class.own-bubble]="isOwnMessage(note.userID)"
                    [innerHTML]="note.notes">
                  </div>

                  <!-- Inline Attachments -->
                  <div *ngIf="note.inlineAttachments?.length" class="inline-attachments">
                    <div
                      *ngFor="let att of note.inlineAttachments"
                      class="inline-attachment-item"
                      [class.image-attachment]="isImageAttachment(att)">

                      <!-- Image preview -->
                      <ng-container *ngIf="isImageAttachment(att)">
                        <div class="attachment-image-preview" (click)="downloadInlineAttachment(att)">
                          <img [src]="att.blobUrl" [alt]="att.name" />
                        </div>
                      </ng-container>

                      <!-- File attachment -->
                      <ng-container *ngIf="!isImageAttachment(att)">
                        <div class="attachment-file" (click)="downloadInlineAttachment(att)">
                          <mat-icon [class]="'file-icon ' + getFileIconClass(att.name)">
                            {{ getFileIcon(att.name) }}
                          </mat-icon>
                          <div class="attachment-file-info">
                            <span class="attachment-file-name">{{ att.name }}</span>
                            <span class="attachment-file-size">{{ formatFileSize(att.fileSize) }}</span>
                          </div>
                          <button
                            mat-icon-button
                            class="attachment-download-btn"
                            matTooltip="Download"
                            (click)="downloadInlineAttachment(att); $event.stopPropagation()">
                            <mat-icon>download</mat-icon>
                          </button>
                        </div>
                      </ng-container>

                      <!-- Delete button (own messages only) -->
                      <button
                        *ngIf="isOwnMessage(note.userID)"
                        mat-icon-button
                        class="attachment-delete-btn"
                        matTooltip="Remove attachment"
                        (click)="deleteInlineAttachment(note, att); $event.stopPropagation()">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </div>

                  <!-- Reactions -->
                  <div *ngIf="note.reactionSummary?.length" class="reactions-row">
                    <button
                      *ngFor="let reaction of note.reactionSummary"
                      class="reaction-badge"
                      [class.own-reaction]="reaction.hasCurrentUserReacted"
                      [matTooltip]="getReactionTooltip(reaction)"
                      (click)="toggleReaction(note, reaction.emoji)">
                      <span class="reaction-emoji">{{ reaction.emoji }}</span>
                      <span class="reaction-count">{{ reaction.count }}</span>
                    </button>
                  </div>

                  <!-- Message actions -->
                  <div class="message-actions">
                    <div class="reaction-picker-container">
                      <button
                        mat-icon-button
                        class="action-btn"
                        matTooltip="React"
                        (click)="toggleReactionPicker(note); $event.stopPropagation()">
                        <mat-icon>emoji_emotions</mat-icon>
                      </button>
                      <div *ngIf="activeReactionPickerNoteId === note.id" class="reaction-picker">
                        <button
                          *ngFor="let emoji of reactionEmojis"
                          class="emoji-btn"
                          (click)="addReaction(note, emoji); $event.stopPropagation()">
                          {{ emoji.emoji }}
                        </button>
                      </div>
                    </div>

                    <button
                      mat-icon-button
                      class="action-btn"
                      matTooltip="Reply"
                      (click)="replyToNote(note)">
                      <mat-icon>reply</mat-icon>
                    </button>

                    <ng-container *ngIf="isOwnMessage(note.userID)">
                      <button
                        mat-icon-button
                        class="action-btn"
                        matTooltip="Edit"
                        (click)="editNote(note)">
                        <mat-icon>edit</mat-icon>
                      </button>
                      <button
                        mat-icon-button
                        class="action-btn danger"
                        matTooltip="Delete"
                        (click)="deleteNote(note)">
                        <mat-icon>delete_outline</mat-icon>
                      </button>
                    </ng-container>
                  </div>
                </div>

                <!-- Avatar (right side for own) -->
                <div *ngIf="isOwnMessage(note.userID)" class="message-avatar">
                  <span class="avatar-text tiny own-avatar">
                    {{ getInitials(note.userID) }}
                  </span>
                </div>
              </div>
            </div>
          </ng-container>
        </div>

        <!-- Message Input Area -->
        <div class="message-input-area">
          <!-- Reply/Edit indicator -->
          <div *ngIf="noteToReply" class="reply-bar">
            <div class="reply-bar-info">
              <mat-icon *ngIf="!isReplyEdit">reply</mat-icon>
              <mat-icon *ngIf="isReplyEdit">edit</mat-icon>
              <span *ngIf="!isReplyEdit">
                Replying to <strong>{{ getUserName(noteToReply.userID) }}</strong>
              </span>
              <span *ngIf="isReplyEdit">
                Editing message
              </span>
            </div>
            <button mat-icon-button (click)="cancelReply()">
              <mat-icon>close</mat-icon>
            </button>
          </div>

          <!-- Pending file attachments -->
          <div *ngIf="pendingFiles.length > 0" class="pending-files">
            <div *ngFor="let file of pendingFiles; let i = index" class="pending-file-chip">
              <mat-icon>{{ getFileIcon(file.name) }}</mat-icon>
              <span>{{ file.name }}</span>
              <button mat-icon-button (click)="removePendingFile(i)">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>

          <!-- Input row -->
          <div class="input-row">
            <input
              #fileInput
              type="file"
              multiple
              style="display: none"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg,.gif"
              (change)="onFilesSelected($event)" />

            <div class="text-input-wrapper">
              <app-rich-text-editor
                [formControl]="messageControl"
                [users]="users"
                [showAttachButton]="true"
                (mentionUser)="onUserMentioned($event)"
                (attachFile)="triggerFileUpload()"
                placeholder="Type a message..."
                minHeight="80px">
              </app-rich-text-editor>
            </div>

            <button
              mat-icon-button
              class="send-btn"
              [class.active]="messageControl.value?.trim()"
              [disabled]="!messageControl.value?.trim()"
              matTooltip="Send"
              (click)="submitMessage()">
              <mat-icon>send</mat-icon>
            </button>
          </div>
        </div>
      </ng-container>

      <!-- ======================== AUTO INFO TAB ======================== -->
      <ng-container *ngIf="activeTab === 'auto_info'">
        <div class="auto-info-container">
          <div *ngIf="isLoadingThread" class="loading-container">
            <mat-spinner diameter="32"></mat-spinner>
          </div>

          <div *ngIf="!isLoadingThread && systemNotes.length === 0" class="empty-state">
            <mat-icon class="empty-icon">smart_toy</mat-icon>
            <p>No automated information</p>
            <span class="empty-hint">System-generated details will appear here</span>
          </div>

          <div *ngIf="!isLoadingThread && systemNotes.length > 0" class="system-notes-list">
            <div *ngFor="let note of systemNotes; trackBy: trackByNoteId" class="system-note-card">
              <div class="system-note-header">
                <div class="system-note-icon">
                  <mat-icon>smart_toy</mat-icon>
                </div>
                <span class="system-note-label">System</span>
                <span class="system-note-time">{{ formatDate(note.date) }}</span>
              </div>
              <div class="system-note-body" [innerHTML]="note.notes"></div>

              <!-- Inline Attachments on system notes -->
              <div *ngIf="note.inlineAttachments?.length" class="inline-attachments">
                <div *ngFor="let att of note.inlineAttachments" class="inline-attachment-item">
                  <div class="attachment-file" (click)="downloadInlineAttachment(att)">
                    <mat-icon [class]="'file-icon ' + getFileIconClass(att.name)">
                      {{ getFileIcon(att.name) }}
                    </mat-icon>
                    <div class="attachment-file-info">
                      <span class="attachment-file-name">{{ att.name }}</span>
                      <span class="attachment-file-size">{{ formatFileSize(att.fileSize) }}</span>
                    </div>
                    <button mat-icon-button class="attachment-download-btn" matTooltip="Download"
                            (click)="downloadInlineAttachment(att); $event.stopPropagation()">
                      <mat-icon>download</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- ======================== DOCUMENTS TAB ======================== -->
      <ng-container *ngIf="activeTab === 'documents'">
        <div class="documents-container">
          <div class="documents-header">
            <span class="documents-title">
              Documents ({{ dmsFiles.length + threadAttachments.length }})
            </span>
            <button
              mat-flat-button
              class="upload-btn"
              (click)="openUploadDialog()">
              <mat-icon>cloud_upload</mat-icon>
              Upload
            </button>
          </div>

          <!-- DMS Files -->
          <div *ngIf="dmsFiles.length > 0" class="documents-section">
            <span class="section-label">Important Documents</span>
            <div
              *ngFor="let doc of dmsFiles"
              class="document-item"
              (click)="viewDocument(doc)">
              <mat-icon [class]="'file-icon ' + getFileIconClass(doc.originalName)">
                {{ getFileIcon(doc.originalName) }}
              </mat-icon>
              <div class="document-info">
                <span class="document-name">{{ doc.originalName }}</span>
                <span class="document-meta">{{ formatFileSize(doc.size) }} &bull; {{ doc.uploadedAt | date:'dd/MM/yyyy' }}</span>
              </div>
              <button mat-icon-button matTooltip="Download" (click)="downloadDocument(doc); $event.stopPropagation()">
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>

          <!-- Conversation Attachments -->
          <div *ngIf="threadAttachments.length > 0" class="documents-section">
            <span class="section-label">Conversation Attachments</span>
            <div
              *ngFor="let att of threadAttachments"
              class="document-item">
              <mat-icon [class]="'file-icon ' + getFileIconClass(att.fileName)">
                {{ getFileIcon(att.fileName) }}
              </mat-icon>
              <div class="document-info">
                <span class="document-name">{{ att.fileName }}</span>
                <span class="document-meta">
                  {{ formatFileSize(att.fileSize) }} &bull; {{ att.uploadedDate | date:'dd/MM/yyyy' }}
                  &bull; by {{ getUserName(att.uploadedByUserId) }}
                </span>
              </div>
              <button mat-icon-button matTooltip="Download" (click)="downloadConversationAttachment(att)">
                <mat-icon>download</mat-icon>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div *ngIf="dmsFiles.length === 0 && threadAttachments.length === 0" class="empty-state">
            <mat-icon class="empty-icon">folder_open</mat-icon>
            <p>No documents yet</p>
            <span class="empty-hint">Upload files or attach them in the conversation</span>
          </div>

          <!-- Upload Drop Zone -->
          <div class="upload-zone" (click)="openUploadDialog()">
            <mat-icon>cloud_upload</mat-icon>
            <span><strong>Click to upload</strong> or drag and drop</span>
            <span class="upload-hint">PDF, DOC, DOCX, XLS, XLSX, ZIP up to 10MB</span>
          </div>
        </div>
      </ng-container>

      <!-- ======================== DETAILS TAB ======================== -->
      <ng-container *ngIf="activeTab === 'details'">
        <div class="details-container">
          <div class="info-card">
            <h5>Note Information</h5>
            <div class="info-row">
              <span class="info-label">Reference</span>
              <span class="info-value">
                <app-reference-number [referenceNumber]="selectedNote.referenceNumber"></app-reference-number>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">Client</span>
              <span class="info-value clickable client-link" (click)="openClientPage(selectedNote)">
                {{ selectedNote.clientName }}
              </span>
            </div>
            <div class="info-row" *ngIf="getCompanyName(noteThread?.companyId)">
              <span class="info-label">Company</span>
              <span class="info-value">{{ getCompanyName(noteThread?.companyId) }}</span>
            </div>
            <div class="info-row" *ngFor="let item of [
              { label: 'Code', value: selectedNote.clientCode },
              { label: 'Title', value: selectedNote.title },
              { label: 'Department', value: selectedNote.departmentName },
              { label: 'Service', value: selectedNote.serviceName },
              { label: 'Year', value: selectedNote.year },
              { label: 'Status', value: selectedNote.status },
              { label: 'Created', value: formatDate(selectedNote.createdDate) },
              { label: 'Last Activity', value: formatDate(selectedNote.lastActivityDate) },
              { label: 'Total Replies', value: selectedNote.totalReplies }
            ]">
              <span class="info-label">{{ item.label }}</span>
              <span class="info-value" [class.status-chip]="item.label === 'Status'">
                {{ item.value }}
              </span>
            </div>
          </div>

          <div class="info-card">
            <h5>Participants ({{ threadParticipantIds.length }})</h5>
            <div
              *ngFor="let userId of threadParticipantIds"
              class="participant-row">
              <span
                class="avatar-text tiny"
                [style.background-color]="'hsl(' + (userId * 73 % 360) + ', 50%, 50%)'">
                {{ getInitials(userId) }}
              </span>
              <span class="participant-name">{{ getUserName(userId) }}</span>
              <span *ngIf="isOwnMessage(userId)" class="you-badge">You</span>
            </div>
          </div>
        </div>
      </ng-container>

      <!-- ======================== ACTIVITY TAB ======================== -->
      <ng-container *ngIf="activeTab === 'activity'">
        <div class="activity-container">
          <h5 class="activity-title">Recent Activity</h5>

          <div *ngIf="activities.length === 0" class="empty-state">
            <mat-icon class="empty-icon">history</mat-icon>
            <p>No activity recorded yet</p>
          </div>

          <div *ngFor="let activity of activities" class="activity-item">
            <div class="activity-icon-wrapper">
              <mat-icon>{{ getActivityIcon(activity.type) }}</mat-icon>
            </div>
            <div class="activity-content">
              <div class="activity-text">
                <strong>{{ getUserName(activity.userId) }}</strong>&nbsp;
                <span class="activity-action">{{ activity.detail }}</span>
              </div>
              <span class="activity-time">{{ formatDate(activity.timestamp) }}</span>
            </div>
          </div>
        </div>
      </ng-container>

    </div>
  </div>
</div>
